/**
 * Toda resposta de erro da Api segue RFC7807 (ProblemDetails) com as extensoes
 * `errorCode` e `correlationId` (docs/specification/11-BACKEND-API.md secao 4).
 * Nunca contem stacktrace, SQL ou segredo.
 */
export interface ProblemDetails {
  readonly title: string
  readonly status: number
  readonly errorCode: string
  readonly correlationId: string
  readonly detail?: string
  /** Presente em respostas de validacao (422/400): campo/codigo -> mensagens. */
  readonly errors?: Readonly<Record<string, readonly string[]>>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function parseErrors(value: unknown): Readonly<Record<string, readonly string[]>> | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const entries: Array<[string, readonly string[]]> = []

  for (const [key, messages] of Object.entries(value)) {
    if (isStringArray(messages)) {
      entries.push([key, messages])
    }
  }

  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

/**
 * Converte o corpo de erro (ja desserializado pelo openapi-fetch) em ProblemDetails
 * conhecido, com fallback honesto quando o formato nao bate (rede fora do ar, proxy, etc).
 */
export function parseProblemDetails(value: unknown, fallbackStatus: number): ProblemDetails {
  if (
    isRecord(value) &&
    typeof value['title'] === 'string' &&
    typeof value['status'] === 'number'
  ) {
    const errorCode = typeof value['errorCode'] === 'string' ? value['errorCode'] : 'internal_error'
    const correlationId = typeof value['correlationId'] === 'string' ? value['correlationId'] : ''
    const detail = typeof value['detail'] === 'string' ? value['detail'] : undefined
    const errors = parseErrors(value['errors'])

    return {
      title: value['title'],
      status: value['status'],
      errorCode,
      correlationId,
      ...(detail !== undefined ? { detail } : {}),
      ...(errors !== undefined ? { errors } : {}),
    }
  }

  return {
    title: 'Nao foi possivel completar a solicitacao.',
    status: fallbackStatus,
    errorCode: 'internal_error',
    correlationId: '',
  }
}

/** Erro tipado lancado por toda chamada de api/*.ts em caso de resposta nao-OK. */
export class ApiError extends Error {
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(problem.title)
    this.name = 'ApiError'
    this.problem = problem
  }
}

/** Lista plana de mensagens para exibicao (titulo + eventuais mensagens de validacao). */
export function describeApiError(error: unknown): readonly string[] {
  if (error instanceof ApiError) {
    const fieldMessages = error.problem.errors ? Object.values(error.problem.errors).flat() : []
    return fieldMessages.length > 0 ? fieldMessages : [error.problem.title]
  }

  return []
}
